import { Grid, Typography, Checkbox, FormControlLabel } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import * as React from "react";
import { KeyValue, KeyValueObject, Query } from "../../interfaces";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import clsx from "clsx";
interface Props {
  appState: Query;
  structure: KeyValue;
  int: number;
  setSubfields: (setFields: KeyValueObject[]) => void;
  checkboxState: KeyValue;
  setCheckboxState: (setCheckboxes: KeyValue) => void;
  disable: boolean;
  data: KeyValue[] | undefined;
  selectedCheckboxes: string[];
  setSelectedCheckboxes: (setSelected: string[]) => void;
  ignoredVariables: string[];
}
const useStyles = makeStyles(theme => ({
  treeItem: {
    "& .treeVariable": {
      textAlign: "right",
      paddingRight: "6rem",
    },
    "& .MuiTreeItem-content, & .MuiTreeItem-label": {
      "padding": 0,
      "borderRadius": 3,
      "& *": {
        color: theme.palette.primary.main,
      },
    },
  },

  expandable: {
    "& .MuiTreeItem-group": {
      marginLeft: 35,
    },
    "& li[aria-expanded] > .MuiTreeItem-content": {
      "backgroundColor": theme.palette.secondary.main,
      "marginBottom": 8,
      "& .treeVariable > p": {
        marginTop: 8,
      },
      "& p, & span, & .MuiTreeItem-iconContainer > svg": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiTreeItem-label": {
      "& .noCheckBox label": {
        padding: 9,
      },
    },
    "& > .MuiTreeItem-content": {
      "backgroundColor": theme.palette.secondary.main,
      "marginBottom": 8,
      "& .treeVariable > p": {
        marginTop: 8,
      },
    },
    "& .MuiTreeItem-content": {
      "position": "relative",
      "& .MuiTreeItem-iconContainer": {
        "position": "absolute",
        "zIndex": 20,
        "right": 6,
        "& svg": {
          fontSize: "2rem",
        },
      },
    },
  },
  checkAll: {
    "&.Mui-disabled svg": { fill: "#AAAAAA" },
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  singleCheckbox: {
    "&.Mui-disabled svg": { fill: "#AAAAAA" },
  },
}));
function RenderCustomTree({
  appState,
  structure,
  int,
  checkboxState,
  disable,
  setSubfields,
  data,
  selectedCheckboxes,
  setSelectedCheckboxes,
  setCheckboxState,
  ignoredVariables,
}: Props) {
  const classes = useStyles();
  const checkAllSubFields = (
    subFields: KeyValue[],
    event: React.ChangeEvent<HTMLInputElement>,
    integer: number,
    arrayVal?: KeyValueObject[],
  ) => {
    const array: KeyValueObject[] = arrayVal
      ? arrayVal
      : [{ key: event.target.name, value: event.target.checked }];
    subFields.forEach((fieldVal: KeyValue) => {
      const allVariables = getCustomReport(fieldVal);
      array.push({
        key: allVariables,
        value: event.target.checked,
      });
      if (fieldVal.sub_fields) {
        return checkAllSubFields(
          fieldVal.sub_fields,
          event,
          integer + 1,
          array,
        );
      } else {
        return setSubfields(array);
      }
    });
  };

  const currentSelectedType = `${appState.ui.incomeBasis[0]}_${appState.ui.unitType[0]}`;
  const reportTypeLabels = appState.ui.reportStructure
    ? appState.ui.reportStructure.map(
        (reportStructure: KeyValue) => reportStructure.label,
      )
    : [""];
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Object.keys(checkboxState).includes(event.target.name)) {
      setCheckboxState({
        ...checkboxState,
        [event.target.name]: event.target.checked,
      });

      //removes any potential duplicate values in the array
      const combinedArray = selectedCheckboxes.includes(event.target.name)
        ? selectedCheckboxes
        : [...selectedCheckboxes, event.target.name];
      setSelectedCheckboxes(
        event.target.checked
          ? combinedArray
          : selectedCheckboxes.filter(
              (selected: string) => selected !== event.target.name,
            ),
      );
    }
  };
  const getCustomReport = (field: KeyValue) => {
    const options: KeyValue = {
      "YTD_$": field.variable_ytd,
      "YTD_%": field.variable_ytdr,
      "Qtly_$": field.variable_qtly,
      "Qtly_%": field.variable_qtlyr,
    };
    return options[currentSelectedType];
  };

  const renderTree = (
    treeFields: KeyValue,
    integer: number,
    hideInt?: boolean,
    numberIndent: number = 0,
  ) => {
    const customReportVar = getCustomReport(treeFields);
    //This resolves the issue with duplicate ID's in the table if the variables are TBD
    const stringToId = (value: string) =>
      value
        .toLowerCase()
        .replace(/,/g, "")
        .replace(/ /g, "-");

    const fieldId = customReportVar
      ? `${customReportVar}_${integer}`
      : `${stringToId(treeFields.label)}_${integer}`;

    const expandableClass =
      Array.isArray(treeFields.sub_fields) && !hideInt
        ? classes.expandable
        : undefined;

    if (Array.isArray(treeFields.sub_fields)) {
      numberIndent++;
    } else {
      numberIndent = 0;
    }

    const subTreeItem =
      Array.isArray(treeFields.sub_fields) &&
      treeFields.sub_fields.some(field => getCustomReport(field))
        ? treeFields.sub_fields.map((subField: KeyValue, subInt: number) => {
            const hasSubFields = Array.isArray(subField.sub_fields);
            if (hasSubFields) {
              return !getCustomReport(subField) &&
                subField.sub_fields.every(
                  (field: KeyValue) => !getCustomReport(field),
                )
                ? null
                : renderTree(subField, subInt, true, numberIndent);
            }
            return renderTree(subField, subInt, true, numberIndent);
          })
        : null;

    const subFieldsChecked = (
      subField: KeyValue[],
      varArray: boolean[],
      indeterminate?: boolean,
    ) => {
      const value: any = subField.map((field: KeyValue) => {
        const subValues = getCustomReport(field);
        Object.keys(checkboxState).includes(getCustomReport(field)) &&
          varArray.push(checkboxState[subValues]);
        if (Array.isArray(field.sub_fields)) {
          return subFieldsChecked(field.sub_fields, varArray);
        } else {
          return varArray;
        }
      });
      const concatValue: boolean[] = [].concat.apply([], value);
      return indeterminate
        ? concatValue.includes(true) && concatValue.includes(false)
        : concatValue.every((val: boolean) => val === true);
    };

    const selectAllProps = {
      style: reportTypeLabels.includes(treeFields.label)
        ? ({ left: -12 } as React.CSSProperties)
        : ({
            position: "absolute",
            left: -35 * (numberIndent - 1),
          } as React.CSSProperties),
      includes: Object.keys(checkboxState).includes(customReportVar)
        ? [checkboxState[customReportVar]]
        : [],
      inputProps: {
        "aria-label": `${
          customReportVar
            ? customReportVar
            : treeFields.label && stringToId(treeFields.label)
        }_all`,
      },
      name: `${
        customReportVar
          ? customReportVar
          : treeFields.label && stringToId(treeFields.label)
      }_all`,
    };

    const selectAllCheckbox =
      Array.isArray(treeFields.sub_fields) &&
      getCustomReport(treeFields.sub_fields[0]) ? (
        <Checkbox
          key={`${customReportVar}_all`}
          disabled={disable}
          style={selectAllProps.style}
          tabIndex={0}
          className={classes.checkAll}
          checked={subFieldsChecked(
            treeFields.sub_fields,
            selectAllProps.includes,
          )}
          indeterminate={subFieldsChecked(
            treeFields.sub_fields,
            selectAllProps.includes,
            true,
          )}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            checkAllSubFields(treeFields.sub_fields, e, 0, [
              { key: customReportVar, value: e.target.checked },
            ]);
          }}
          color="secondary"
          inputProps={selectAllProps.inputProps}
          name={selectAllProps.name}
        />
      ) : (
        undefined
      );
    const messageLookup: KeyValue = {
      "Performance and Condition Ratios":
        "Only Available with Unit Type Percentages",
      "Assets, Liabilities, and Capital":
        "Only Available with Income Basis Year to Date",
    };
    const treeItemProps = {
      textClass:
        !customReportVar && Array.isArray(treeFields.sub_fields)
          ? "noCheckBox"
          : "",
      control: (
        <>
          {reportTypeLabels.includes(treeFields.label) ||
          ((!customReportVar || ignoredVariables.includes(customReportVar)) &&
            Array.isArray(treeFields.sub_fields)) ? (
            undefined
          ) : (
            <Checkbox
              disabled={disable}
              tabIndex={0}
              checked={checkboxState[customReportVar]}
              className={classes.singleCheckbox}
              onClick={e => e.stopPropagation()}
              onChange={e => handleChange(e)}
              color="primary"
              name={customReportVar}
            />
          )}
        </>
      ),
      label:
        Array.isArray(treeFields.sub_fields) &&
        !getCustomReport(treeFields.sub_fields[0]) ? (
          <span id={treeFields.label && stringToId(treeFields.label)}>
            {treeFields.label}: <b>{messageLookup[treeFields.label]}</b>
          </span>
        ) : (
          <span id={treeFields.label && stringToId(treeFields.label)}>
            {treeFields.label}
          </span>
        ),
    };
    return data &&
      ((customReportVar && !ignoredVariables.includes(customReportVar)) ||
        Array.isArray(treeFields.sub_fields)) ? (
      <TreeItem
        className={clsx(expandableClass, classes.treeItem)}
        id={fieldId}
        collapseIcon={<ExpandLessIcon id={`${fieldId}_collapse`} />}
        expandIcon={<ExpandMoreIcon id={`${fieldId}_expand`} />}
        tabIndex={0}
        TransitionProps={{
          exit: false,
          enter: false,
        }}
        key={`${fieldId}_${integer}`}
        nodeId={`${fieldId}_${integer}`}
        label={
          <Grid container style={{ flexWrap: "nowrap" }}>
            <Grid item xs={8} className={"treeData"} style={{ marginLeft: 8 }}>
              <Typography
                variant={"body1"}
                className={treeItemProps.textClass}
                style={{ fontSize: 16 }}
              >
                {selectAllCheckbox}

                <FormControlLabel
                  control={treeItemProps.control}
                  label={treeItemProps.label}
                />
              </Typography>
            </Grid>
            <Grid item xs={4} className={"treeData treeVariable"}>
              <Typography variant={"body1"} style={{ fontSize: 16 }}>
                {customReportVar}
              </Typography>
            </Grid>
          </Grid>
        }
      >
        {subTreeItem}
      </TreeItem>
    ) : (
      <div key={integer}></div>
    );
  };

  return renderTree(structure, int);
}

//export as memo to prevent unnecessary reload
export default React.memo(
  RenderCustomTree,
  (previousProps: Props, nextProps: Props) => {
    return (
      previousProps.checkboxState === nextProps.checkboxState &&
      JSON.stringify(previousProps.appState.ui) ===
        JSON.stringify(nextProps.appState.ui)
    );
  },
);
